import {SubmitHandler, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {COUNTRIES} from 'tally-validation';
import {object, string} from 'yup';

import {useUpdateCustomerAddressMutation} from '#apollo/schema';
import {Button, Form, Select, Text} from '#component/base';
import {TextInputField} from '#component/partial';
import {useToastQueue} from '#hook';
import {sprinkles} from '#style/sprinkles';
import {handleValidationError} from '#util';
import {ValidationError} from '#util/validator/handleValidationError';

import {ShippingAddressForm, ShippingAddressFormProps} from './FormEditShipping.types';

import {
  firstLineWrapper,
  formWrapper,
  itemFieldCustom,
  selectWrapper,
} from './FormEditShipping.css';

const states = COUNTRIES.filter(country => country.code === 'US')[0].provinces;
const statesOptions = states.map(state => ({label: state.name, value: state.code}));

const FormEditShipping = ({shippingAddress, handleCancel}: ShippingAddressFormProps) => {
  const [updateCustomerAddressMutation] = useUpdateCustomerAddressMutation({
    refetchQueries: ['ProfileInfoBoxQuery'],
  });
  const {addToast} = useToastQueue();
  const {address, addressDetail, postalCode, city, state, country} = shippingAddress;

  const {
    clearErrors,
    formState: {errors, isSubmitting},
    handleSubmit: hookFormHandleSubmit,
    register,
    setError,
  } = useForm<ShippingAddressForm>({
    defaultValues: {
      address,
      addressDetail: addressDetail || '',
      postalCode,
      city,
      state,
      country,
    },
    mode: 'onBlur',
    resolver: yupResolver(
      object({
        address: string().required(),
        addressDetail: string(),
        postalCode: string().required(),
        city: string().required(),
        state: string().required(),
        country: string().required(),
      }),
    ),
  });
  const handleFormSubmit: SubmitHandler<ShippingAddressForm> = async ({
    address,
    addressDetail,
    postalCode,
    city,
    state,
    country,
  }) => {
    try {
      await updateCustomerAddressMutation({
        variables: {
          input: {
            address,
            addressDetail,
            postalCode,
            city,
            provinceCode: state,
            countryCode: country,
          },
        },
      });
      addToast({children: 'Shipping address updated'});
      handleCancel();
    } catch (err) {
      handleValidationError({
        error: err as unknown as ValidationError<ShippingAddressForm>,
        setError,
      });
    }
  };
  return (
    <Form
      className={formWrapper}
      clearErrors={() =>
        clearErrors(['address', 'addressDetail', 'postalCode', 'city', 'state', 'country'])
      }
      errors={errors}
      onSubmit={hookFormHandleSubmit(handleFormSubmit)}>
      <div>
        <TextInputField label="Address" {...register('address')} />
      </div>
      <div>
        <TextInputField label="Apartment, Suite, etc." {...register('addressDetail')} />
      </div>
      <div className={firstLineWrapper}>
        <TextInputField label="Postal Code" {...register('postalCode')} />
        <TextInputField label="City" {...register('city')} />
      </div>
      <div className={firstLineWrapper}>
        <div className={selectWrapper}>
          <label className={sprinkles({content: 'between', flex: 'flex'})} htmlFor={state}>
            <Text color="doveGray" variant="label">
              state
            </Text>
          </label>
          <Select
            label="State"
            className={itemFieldCustom}
            {...register('state')}
            options={statesOptions}
          />
        </div>
        <TextInputField
          label="Country"
          type="select"
          className={itemFieldCustom}
          value="US"
          options={[
            {
              label: 'United States',
              value: 'US',
            },
          ]}
          {...register('country')}
        />
      </div>
      <div className={firstLineWrapper}>
        <Button type="button" variant="secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          Save
        </Button>
      </div>
    </Form>
  );
};

export default FormEditShipping;
